/**
 * Шаги ролика «Видео» и «Монтаж» (`?tab=video` и `?tab=cut`). Раньше это была одна вкладка с четырьмя
 * панелями подряд; задача Оли №331 — «разделить по табам шаги»: снять клипы и собрать ролик — разная
 * работа, и порядок должен читаться по экрану.
 *
 * Данные у обоих шагов одни — опрос `GET /api/projects/{id}/video` (`useProjectVideo`: раз в 5 с, пока
 * клипы в работе или монтаж собирается); до первого ответа — хронология и монтажи из карточки
 * проекта (`video`). Опрос общий: react-query отдаёт обоим один и тот же ключ.
 */

import { ErrorLine, Hint } from "@/components/project/ui";
import { errorText } from "@/lib/project";
import { useProjectVideo, type VideoView } from "@/lib/video";

import { ClipPromptsPanel } from "./ClipPromptsPanel";
import { ClipsTable } from "./ClipsTable";
import { ComparePanel } from "./ComparePanel";
import { CutsPanel } from "./CutsPanel";
import { VideoRunPanel } from "./VideoRunPanel";

/** Шаг «Видео»: из каждого готового кадра — клип, кадр ракурса становится его стартовым фреймом. */
export function ClipsTab({ pid, video }: { pid: number; video: VideoView }) {
  const poll = useProjectVideo(pid);
  const timeline = poll.data?.timeline ?? video.timeline;
  const pending = poll.data?.pending_clips ?? video.pending_clips;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4">
      {poll.isError ? <ErrorLine text={`опрос клипов: ${errorText(poll.error)}`} /> : null}
      {/* Промпт движения — здесь, рядом с запуском клипов (№335): на шаге «Ракурсы» он путался
          с заданием ракурса, которое описывает картинку, а не движение. */}
      <ClipPromptsPanel pid={pid} timeline={timeline} />
      <VideoRunPanel pid={pid} timeline={timeline} costCap={video.cost_cap} />
      <ClipsTable pid={pid} timeline={timeline} pending={pending} />
    </div>
  );
}

/** Шаг «Монтаж»: сборка ролика из готовых клипов и сравнение монтажей по моделям. */
export function CutTab({ pid, video }: { pid: number; video: VideoView }) {
  const poll = useProjectVideo(pid);
  const timeline = poll.data?.timeline ?? video.timeline;
  const cuts = poll.data?.cuts ?? video.cuts;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4">
      {poll.isError ? <ErrorLine text={`опрос монтажей: ${errorText(poll.error)}`} /> : null}
      <CutsPanel pid={pid} timeline={timeline} cuts={cuts} />
      {/* `compare` живёт только в опросе (отката в карточке проекта у него нет): до первого ответа
          панель утверждала бы «готовых монтажей нет» — на короткой вкладке это заметно (ревью 16.10). */}
      {poll.isPending ? (
        <Hint>сравнение монтажей загружается…</Hint>
      ) : (
        <ComparePanel pid={pid} compare={poll.data?.compare ?? []} cuts={cuts} />
      )}
    </div>
  );
}
